/**
 * Consumer-ledger vocabulary: the words the ledger endpoint speaks and the
 * shapes every surface reads them in.
 *
 * App-level rather than component-level because none of it belongs to one
 * table. The user page's Transaction History, the Reports → Transactions
 * screen and the support ticket's linked-transaction row all read
 * `processed_status`, `transfer_type` and `destination_account_source` off the
 * same payload, and they have to agree on what those values mean.
 *
 * The table-state vocabulary at the bottom of this file — sortable columns, the
 * filter options, the column-key → `sortBy` map — lives here for the same
 * reason. Its only reader is `utils/ledger.js`, and a helper in `utils/`
 * reaching into `components/common/ledgerTable/` for a constant points the
 * dependency the wrong way: `src/constants/` and `src/utils/` are what the rest
 * of the app is allowed to depend on, not the other way round.
 */

/**
 * Why a row has no processed date, as the endpoint reports it in
 * `processed_status`. The server decides this — a view only picks the words.
 */
export const LEDGER_PROCESSED_STATUS = Object.freeze({
    PROCESSED: 'processed',
    PENDING: 'pending',
    UNKNOWN: 'unknown',
});

/**
 * Advance types as `transfer_type` reports them. `fee199` never reaches here —
 * the service normalises it to `standard` when it writes the transfer.
 */
export const ADVANCE_TYPE = Object.freeze({
    INSTANT: 'instant',
    STANDARD: 'standard',
});

/**
 * Labels for the ADVANCE TYPE column. Only advances belong here: a settlement
 * row also carries a transfer (`transfer_type: 'settle'`), but it is not an
 * advance, so it falls through to the placeholder rather than reading "Settle"
 * under a column titled Advance Type.
 */
export const ADVANCE_TYPE_LABELS = Object.freeze({
    [ADVANCE_TYPE.INSTANT]: 'Instant',
    [ADVANCE_TYPE.STANDARD]: 'Standard',
});

/**
 * Human labels for the ledger `type` enum.
 *
 * One map, read by both surfaces that name a type: the `TransactionTypeTag`
 * pill in the TYPE column and the filter dropdown above it
 * ({@link LEDGER_TYPE_FILTER_OPTIONS}, which derives its option text from
 * here). Two copies let the dropdown and the pill drift into different words
 * for the same row.
 */
export const LEDGER_TYPE_LABELS = Object.freeze({
    advance: 'Advance',
    settlement: 'Settlement',
    service_fee: 'Service Fee',
});

/** Where the destination account came from (see the API's `destination_account_source`). */
export const DESTINATION_SOURCE = Object.freeze({
    /** Snapshotted on the transfer — where the money actually went. */
    TRANSFER: 'transfer',
    /** The member's current direct-deposit account — where it would go today. */
    CURRENT_DDA: 'current_dda',
});

/**
 * `?reconciliation=` on the consumer ledger endpoint. Mirrors
 * `Constants.LEDGER_RECONCILIATION_FILTER` in clockout-service. INCLUDE is the
 * server default and is never sent.
 */
export const LEDGER_RECONCILIATION_FILTER = Object.freeze({
    INCLUDE: 'include',
    EXCLUDE: 'exclude',
    ONLY: 'only',
});

/** What a cell shows when the value is genuinely unknown. */
export const LEDGER_TABLE_PLACEHOLDER = '—';

/** A standard advance is inserted unsettled and backfilled later; that is a state, not a gap. */
export const LEDGER_PENDING_LABEL = 'Pending';

/** Shown in the User column when the account row is gone or nameless. */
export const LEDGER_USER_UNKNOWN_LABEL = 'Unknown user';

/**
 * Shown when neither the transfer snapshot nor the member's current account
 * yields a destination. Says "unknown" rather than leaving a dash: a blank here
 * would read as "no destination", and money always went somewhere.
 */
export const LEDGER_DESTINATION_UNKNOWN_LABEL = 'Unknown';

/**
 * The zone ledger timestamps are rendered in — fixed, never the viewer's.
 *
 * Two people in different zones must read the same row the same way; once the
 * cell shows seconds, a viewer-local time silently means something different to
 * whoever is on the other end of the support conversation. Fixing the zone is
 * what buys that, and the zone is named where it is visible without hovering:
 * the Created / Processed column headings carry `LEDGER_TIMEZONE_LABEL`, and
 * each cell's `title` repeats it alongside the numeric offset.
 *
 * Eastern because it is the zone the business already reasons in: the
 * settlement window is defined as midnight–10am America/New_York in
 * clockout-service `core/tasks/settleaccount.js`, so a processed time is only
 * checkable against it in that zone.
 *
 * A plain constant, deliberately not a build-time env var, and only a
 * FALLBACK: the ledger endpoint declares the zone it evaluated its date range
 * in (`timezone`, clockout-service #316), and that declaration wins whenever
 * it is present — see `resolveLedgerRenderZone` in `utils/dates.js`. One zone
 * baked per image could not have served two FIs from one build anyway.
 */
export const LEDGER_TIMEZONE = 'America/New_York';

/**
 * How long a zone label may be before it stops reading as a marker in a column
 * heading. `GMT+9` is five characters, and nothing shorter is ever discarded.
 */
const LEDGER_TIMEZONE_LABEL_MAX = 5;

/**
 * A short name for the zone, for the Created / Processed column headings.
 *
 * `shortGeneric` rather than `short` on purpose: it answers `ET`, which holds
 * all year, where `short` answers `EDT` or `EST` depending on the date the
 * label happens to be computed on — and the label is memoised per zone
 * (`formatLedgerTimezoneLabel` in `utils/dates.js`), so a session open across
 * the DST switch would otherwise carry a heading that has quietly become wrong.
 *
 * The heading is where the zone becomes VISIBLE. It is also in each cell's
 * `title`, but a tooltip does not exist on touch and does not survive into a
 * screenshot pasted in a support thread, which is how these rows travel.
 *
 * Only the US zones have a short generic form. `Asia/Tokyo` answers "Japan
 * Time", which is a sentence in a column heading, so anything longer than a
 * marker falls back to `short` (`GMT+9`) — still unambiguous, and it fits. A
 * runtime that gives back nothing usable falls back to the IANA name.
 *
 * @param {string} timezone IANA zone name
 * @returns {string}
 */
export function resolveTimezoneLabel(timezone) {
    const nameIn = (timeZoneName) => {
        try {
            return (
                new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName })
                    .formatToParts(new Date())
                    .find((part) => part.type === 'timeZoneName')?.value || null
            );
        } catch (_error) {
            return null;
        }
    };

    const generic = nameIn('shortGeneric');
    if (generic && generic.length <= LEDGER_TIMEZONE_LABEL_MAX) return generic;
    return nameIn('short') || generic || timezone;
}

export const LEDGER_TIMEZONE_LABEL = resolveTimezoneLabel(LEDGER_TIMEZONE);

/**
 * Date and clock formats for the Created / Processed columns. Split so the two
 * can be stacked in a cell.
 *
 * 24-hour (`HH`) rather than dayjs's `hh`, which is 12-hour zero-padded: with no
 * AM/PM marker `02:41:07` would mean either 2am or 2pm, and these columns are
 * what support reads to place a money movement in time. Same shape as
 * ReviewsTable's `HH:mm`.
 */
export const LEDGER_DATE_FORMAT = 'MM/DD/YYYY';
export const LEDGER_TIME_FORMAT = 'HH:mm:ss';

/**
 * Full timestamp for the cell's `title`: the zone abbreviation plus its numeric
 * offset, so a reader can convert without knowing what EDT stands for.
 */
export const LEDGER_TIMESTAMP_TITLE_FORMAT = 'MM/DD/YYYY HH:mm:ss z (ZZ)';

/** The wire format of `start` / `end` on the ledger endpoint, and of the URL's `dateStart` / `dateEnd`. */
export const LEDGER_API_DATE_FORMAT = 'YYYY-MM-DD';

// ---------------------------------------------------------------------------
// Table state: what the ledger TABLE persists in the query string and sends to
// the endpoint. Read by `utils/ledger.js`, which is what every screen calls —
// no component owns these, which is why they are not beside one.
// ---------------------------------------------------------------------------

/**
 * The `sortBy` values the endpoint accepts, mirroring `Constants.LEDGER_SORT_BY`
 * in clockout-service (`routes/constants.js`), which validates against its own
 * allowlist and answers 400 for anything else.
 *
 * Deliberately a SUBSET of what the backend takes: it also allows
 * `transfer_type`, which reads as a category rather than an ordering, so Advance
 * Type is left unsortable. `type` IS sortable — it doubles as the filter column,
 * and the stored enum happens to order the way the labels read (advance <
 * service_fee < settle). Adding `transfer_type` back is a `sorter: true` on the
 * column plus its key in {@link LEDGER_SORT_BY_COLUMN_KEY} — no backend change.
 */
export const LEDGER_SORT_BY = Object.freeze({
    CREATED_AT: 'created_at',
    TYPE: 'type',
    AMOUNT: 'amount',
    SETTLED_ON: 'settled_on',
});

export const LEDGER_SORT_DIR = Object.freeze({ ASC: 'asc', DESC: 'desc' });

/**
 * What the backend falls back to when no sort is given. Antd clears the sort on
 * the third header click, and "cleared" has to land here rather than on an
 * absent `sortBy`.
 */
export const LEDGER_SORT_DEFAULT = Object.freeze({
    sortBy: LEDGER_SORT_BY.CREATED_AT,
    sortDir: LEDGER_SORT_DIR.DESC,
});

/** Column key -> the `sortBy` value sent to the API. */
export const LEDGER_SORT_BY_COLUMN_KEY = Object.freeze({
    created_at: LEDGER_SORT_BY.CREATED_AT,
    type: LEDGER_SORT_BY.TYPE,
    amount: LEDGER_SORT_BY.AMOUNT,
    processed_at: LEDGER_SORT_BY.SETTLED_ON,
});

/**
 * The filter value for service-fee rows, gated behind `showFees`.
 *
 * Off by default, which lines up with the endpoint: the fee already rides on
 * its advance as a column, and the service hides fee rows unless a type filter
 * asks for them. Offering the option while the fee is also shown inline would
 * present the same money twice.
 */
export const LEDGER_FEE_FILTER_VALUE = 'service_fee';

/**
 * Options for the Transaction Type column filter, in the shape antd's column
 * `filters` takes. The values are the ones the endpoint RETURNS, so
 * `settlement` (not the stored `settle`) is correct — the route maps the
 * display alias back before validating.
 *
 * The text comes from {@link LEDGER_TYPE_LABELS} rather than being spelled out
 * again, so the dropdown and the TYPE pill cannot name the same row
 * differently.
 */
export const LEDGER_TYPE_FILTER_OPTIONS = Object.freeze(
    Object.entries(LEDGER_TYPE_LABELS).map(([value, text]) => Object.freeze({ text, value })),
);

/**
 * Which columns own a query-string filter key, in the shape the design system's
 * `getTableFiltersFromUrl` expects.
 *
 * Kept apart from `getLedgerColumns` so reading the URL never has to build the
 * columns — the URL readers are plain functions in `utils/ledger.js` and have
 * no controller to reach into.
 */
export const LEDGER_FILTERABLE_COLUMNS = Object.freeze([{ key: 'type', filters: true }]);
